// @ts-ignore
import mqtt, { type IClientPublishOptions, type IClientSubscribeOptions, type MqttClient, type Packet } from 'mqtt'
import { matches } from 'mqtt-pattern'
import { v4 as uuidv4 } from 'uuid'
import { env } from '@/utils/env'
import {
  DEFAULT_CONNECTION_STATUS,
  DEFAULT_OPTIONS,
  MQTT_IDLE_DISCONNECT_MS,
  MqttConnectionStatusEnum,
  MqttEventEnum,
  MqttLogMessageEnum
} from './constants'
import type {
  MqttConnectionStatus,
  MqttConnectOptions,
  MqttMessageHandler,
  MqttMessageParser,
  MqttStatusListener,
  MqttSubscribeTarget
} from './types'

type SubscriptionEntry = {
  topic: MqttSubscribeTarget
  handler: MqttMessageHandler<any>
  parser?: MqttMessageParser<any>
}

class MqttRequestClient {
  private client: MqttClient | null = null
  private subscriptions = new Set<SubscriptionEntry>()
  private statusListeners = new Set<MqttStatusListener>()
  private currentUrl = ''
  private currentOptions: MqttConnectOptions = DEFAULT_OPTIONS
  private connectionStatus: MqttConnectionStatus = DEFAULT_CONNECTION_STATUS
  private idleTimer: ReturnType<typeof setTimeout> | null = null
  private readonly clientId = `MVDP_${uuidv4()}`

  private setConnectionStatus(status: MqttConnectionStatus) {
    this.connectionStatus = status
    this.statusListeners.forEach(listener => {
      listener(status)
    })
  }

  private readonly handleMessage = (topic: string, payload: Buffer, packet: Packet) => {
    this.subscriptions.forEach(subscription => {
      const topics = [subscription.topic].flat()

      if (!topics.some(currentTopic => matches(currentTopic, topic))) {
        return
      }

      const parsedPayload = subscription.parser ? subscription.parser(payload) : payload.toString()

      subscription.handler({
        topic,
        payload: parsedPayload,
        packet
      })
    })
  }

  private readonly handleConnect = () => {
    this.setConnectionStatus(MqttConnectionStatusEnum.CONNECTED)
    this.resubscribeAll().catch(error => {
      console.error(MqttLogMessageEnum.FAILED_RESTORE_SUBSCRIPTIONS, error)
    })
  }

  private readonly handleReconnect = () => {
    this.setConnectionStatus(MqttConnectionStatusEnum.RECONNECTING)
  }

  private readonly handleOffline = () => {
    this.setConnectionStatus(MqttConnectionStatusEnum.OFFLINE)
  }

  /**
   * Fires only when mqtt.js ends the client on its own (our disconnect() detaches handlers first), for
   * example after a denied login: mqtt.js does not retry those by default (`reconnectOnConnackError`).
   * Drop the dead client so the next subscribe() creates a fresh one and restores all topics.
   */
  private readonly handleEnd = () => {
    if (this.client) {
      console.warn(MqttLogMessageEnum.CLIENT_ENDED)
      this.detachClient(this.client)
      this.client = null
    }

    this.setConnectionStatus(MqttConnectionStatusEnum.OFFLINE)
  }

  private readonly handleClose = () => {
    this.setConnectionStatus(MqttConnectionStatusEnum.OFFLINE)
  }

  private readonly handleError = (error: Error) => {
    this.setConnectionStatus(error.message || MqttConnectionStatusEnum.OFFLINE)
  }

  private buildUrl(url?: string) {
    return url || env('NEXT_PUBLIC_MQTT_URL') || ''
  }

  private attachClient(client: MqttClient) {
    client.on(MqttEventEnum.CONNECT, this.handleConnect)
    client.on(MqttEventEnum.MESSAGE, this.handleMessage)
    client.on(MqttEventEnum.RECONNECT, this.handleReconnect)
    client.on(MqttEventEnum.OFFLINE, this.handleOffline)
    client.on(MqttEventEnum.END, this.handleEnd)
    client.on(MqttEventEnum.CLOSE, this.handleClose)
    client.on(MqttEventEnum.ERROR, this.handleError)
  }

  private detachClient(client: MqttClient) {
    client.off(MqttEventEnum.CONNECT, this.handleConnect)
    client.off(MqttEventEnum.MESSAGE, this.handleMessage)
    client.off(MqttEventEnum.RECONNECT, this.handleReconnect)
    client.off(MqttEventEnum.OFFLINE, this.handleOffline)
    client.off(MqttEventEnum.END, this.handleEnd)
    client.off(MqttEventEnum.CLOSE, this.handleClose)
    client.off(MqttEventEnum.ERROR, this.handleError)
  }

  private async resubscribeAll() {
    if (!this.client?.connected || this.subscriptions.size === 0) {
      return
    }

    const topics = Array.from(
      new Set(
        Array.from(this.subscriptions).flatMap(subscription => [subscription.topic].flat().map(topic => topic.trim()))
      )
    ).filter(Boolean)

    if (topics.length > 0) {
      await this.subscribeTopics(topics)
    }
  }

  private subscribeTopics(topic: MqttSubscribeTarget, options?: IClientSubscribeOptions) {
    return new Promise<void>((resolve, reject) => {
      this.client?.subscribe(topic, options || {}, error => {
        if (error) {
          reject(error)
          return
        }

        resolve()
      })
    })
  }

  private unsubscribeTopics(topic: MqttSubscribeTarget) {
    return new Promise<void>((resolve, reject) => {
      this.client?.unsubscribe(topic, error => {
        if (error) {
          reject(error)
          return
        }

        resolve()
      })
    })
  }

  private clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer)
      this.idleTimer = null
    }
  }

  /** Closes the client after the grace period unless a subscriber shows up first. */
  private scheduleIdleDisconnect() {
    this.clearIdleTimer()

    if (this.subscriptions.size > 0) {
      return
    }

    this.idleTimer = setTimeout(() => {
      this.idleTimer = null

      if (this.subscriptions.size > 0) {
        return
      }

      this.disconnect().catch(error => {
        console.error(MqttLogMessageEnum.FAILED_DISCONNECT_CLIENT, error)
      })
    }, MQTT_IDLE_DISCONNECT_MS)
  }

  /**
   * Creates the mqtt.js client if there is none and returns it without waiting for the broker.
   * mqtt.js keeps retrying every `reconnectPeriod` until `disconnect()` ends the client.
   */
  private ensureClient(options: MqttConnectOptions = {}): MqttClient {
    const url = this.buildUrl(options.url)

    if (!url) {
      throw new Error(MqttLogMessageEnum.CONNECT_URL_NOT_CONFIGURED)
    }

    if (this.client && url === this.currentUrl) {
      return this.client
    }

    if (this.client) {
      this.disconnect().catch(error => {
        console.error(MqttLogMessageEnum.FAILED_DISCONNECT_CLIENT, error)
      })
    }

    const { url: _, ...mqttOptions } = options
    this.currentUrl = url
    this.currentOptions = { ...DEFAULT_OPTIONS, clientId: this.clientId, ...mqttOptions }
    this.setConnectionStatus(MqttConnectionStatusEnum.CONNECTING)
    console.log(MqttLogMessageEnum.ATTEMPTING_CONNECT, url)

    const client = mqtt.connect(url, this.currentOptions)
    this.client = client
    this.attachClient(client)

    return client
  }

  /** Starts the connection if needed. Does not wait for the broker; closes again if nobody subscribes. */
  async connect(options: MqttConnectOptions = {}): Promise<MqttClient> {
    this.clearIdleTimer()
    const client = this.ensureClient(options)
    this.scheduleIdleDisconnect()

    return client
  }

  async disconnect(force = true): Promise<void> {
    this.clearIdleTimer()

    const activeClient = this.client

    if (!activeClient) {
      return
    }

    this.client = null
    this.detachClient(activeClient)
    this.setConnectionStatus(MqttConnectionStatusEnum.OFFLINE)

    await new Promise<void>(resolve => {
      activeClient.end(force, {}, () => {
        resolve()
      })
    })
  }

  async publish(topic: string, message: string | Buffer | Uint8Array, options?: IClientPublishOptions): Promise<void> {
    this.clearIdleTimer()
    const client = this.ensureClient()
    const payload = typeof message === 'string' || Buffer.isBuffer(message) ? message : Buffer.from(message)

    await new Promise<void>((resolve, reject) => {
      client.publish(topic, payload, options || {}, error => {
        if (error) {
          reject(error)
          return
        }

        resolve()
      })
      this.scheduleIdleDisconnect()
    })
  }

  async subscribe<TMessage = string>(
    topic: MqttSubscribeTarget,
    handler: MqttMessageHandler<TMessage>,
    options?: IClientSubscribeOptions,
    parser?: MqttMessageParser<TMessage>
  ): Promise<() => Promise<void>> {
    this.clearIdleTimer()
    const client = this.ensureClient()

    const subscription: SubscriptionEntry = {
      topic,
      handler,
      parser
    }

    this.subscriptions.add(subscription)

    // While the broker is unreachable the topic is subscribed by resubscribeAll() on the next connect.
    if (client.connected) {
      try {
        await this.subscribeTopics(topic, options)
      } catch (error) {
        this.subscriptions.delete(subscription)
        this.scheduleIdleDisconnect()
        throw error
      }
    }

    return async () => {
      if (!this.subscriptions.delete(subscription)) {
        return
      }

      try {
        const topicStillUsed = Array.from(this.subscriptions).some(item =>
          [item.topic].flat().some(currentTopic => [topic].flat().includes(currentTopic))
        )

        if (!topicStillUsed && this.client?.connected) {
          await this.unsubscribeTopics(topic)
        }
      } finally {
        this.scheduleIdleDisconnect()
      }
    }
  }

  getClient() {
    return this.client
  }

  getClientId() {
    return this.clientId
  }

  getConnectionStatus() {
    return this.connectionStatus
  }

  subscribeStatus(listener: MqttStatusListener) {
    this.statusListeners.add(listener)
    listener(this.connectionStatus)

    return () => {
      this.statusListeners.delete(listener)
    }
  }
}

const mqttClient = new MqttRequestClient()

export default mqttClient
